/**
 * 上传前本地检查 zip 包内文件名是否满足 Registry 契约（减轻保存后才发现缺文件的成本）。
 * 与批量上传、单条 GenericResourceZipUpload 共用；必传/可选以 genericResourceZipPackageSpec 为准。
 */
import JSZip from 'jszip'
import { classifyImageBytes } from './imageKind'
import { resolveRequiredPatterns, resolveZipPackageSpec } from './genericResourceZipPackageSpec'

const LOCAL_FILE_SIG = 0x04034b50

function globToRegex(pattern) {
  const escaped = String(pattern || '')
    .replace(/[.+^${}()|[\]\\]/g, '\\$&')
    .replace(/\*/g, '.*')
  return new RegExp(`^${escaped}$`, 'i')
}

function basename(path) {
  const parts = String(path || '').split('/')
  return parts[parts.length - 1] || path
}

function isIgnoredEntry(name) {
  if (!name || name.endsWith('/')) {
    return true
  }
  if (name.startsWith('__MACOSX/')) {
    return true
  }
  const base = basename(name)
  return base.startsWith('.')
}

/** 扫描 local file header，列出 zip 内文件名（不依赖 JSZip） */
export async function listZipEntryNames(file) {
  const buffer = await file.arrayBuffer()
  const view = new DataView(buffer)
  const names = []
  let offset = 0
  while (offset + 30 <= view.byteLength) {
    if (view.getUint32(offset, true) !== LOCAL_FILE_SIG) {
      offset += 1
      continue
    }
    const compMethod = view.getUint16(offset + 8, true)
    const compSize = view.getUint32(offset + 18, true)
    const nameLen = view.getUint16(offset + 26, true)
    const extraLen = view.getUint16(offset + 28, true)
    const nameStart = offset + 30
    const nameEnd = nameStart + nameLen
    if (nameEnd > view.byteLength) {
      break
    }
    const nameBytes = new Uint8Array(buffer, nameStart, nameLen)
    const name = new TextDecoder('utf-8').decode(nameBytes)
    if (!isIgnoredEntry(name)) {
      names.push(name)
    }
    const dataStart = nameEnd + extraLen
    let next = dataStart + compSize
    if (compMethod !== 0 && compSize === 0) {
      next = offset + 1
    }
    if (next <= offset) {
      offset += 1
    } else {
      offset = next
    }
  }
  return names
}

/**
 * 必传 checklist：pattern 与 includes 按「全量 pattern 下标」对齐后再剔 optional。
 * 若先 filter 再按下标取 includes，optional 夹在中间时会把封面标成「侧视角」等错文案。
 */
export function buildChecklistItems(assetType) {
  const resolved = resolveZipPackageSpec(assetType)
  const includes = resolved.includes || []
  const optional = new Set(resolved.optional || [])
  const patterns = resolved.pattern || []
  const items = []

  // anyOf（stickers）的说明句不进成功卡片。张数和静动混放在 inspectZipStructure 里检查。
  if (!resolved.anyOf) {
    patterns.forEach((pattern, index) => {
      if (optional.has(pattern)) {
        return
      }
      const label = includes[index] || pattern
      items.push({ label, pattern, kind: 'pattern' })
    })
  }

  const requiredExact = resolveRequiredPatterns(resolved).filter((item) =>
    (resolved.exact || []).includes(item)
  )
  requiredExact.forEach((exact) => {
    if (items.some((item) => item.pattern === exact)) {
      return
    }
    items.push({ label: exact, pattern: exact, kind: 'exact' })
  })

  if (!resolved.anyOf && !items.length && includes.length) {
    includes.forEach((label) => {
      items.push({ label, pattern: '*', kind: 'includes' })
    })
  }
  return items
}

function matchPattern(names, pattern) {
  const re = globToRegex(pattern)
  return names.some((name) => re.test(basename(name)))
}

async function classifyStickerZip(file) {
  const images = []
  const bad = []
  let zip
  try {
    zip = await JSZip.loadAsync(await file.arrayBuffer())
  } catch (error) {
    return { images, bad, error: '无法读取 zip 文件，请检查文件是否损坏' }
  }
  const entries = Object.keys(zip.files)
  for (let index = 0; index < entries.length; index += 1) {
    const entry = zip.files[entries[index]]
    if (!entry || entry.dir || isIgnoredEntry(entry.name)) {
      continue
    }
    if (basename(entry.name).toLowerCase().endsWith('.json')) {
      continue
    }
    let bytes
    try {
      bytes = await entry.async('uint8array')
    } catch (error) {
      bad.push(`${basename(entry.name)} 读不出内容`)
      continue
    }
    const kind = classifyImageBytes(bytes)
    if (!kind) {
      bad.push(`${basename(entry.name)} 不是 png、jpg、gif、webp`)
      continue
    }
    images.push(kind)
  }
  return { images, bad, error: '' }
}

/**
 * @returns {Promise<{ ok: boolean, items: Array<{label:string, ok:boolean}>, missing: string[], entryCount: number }>}
 */
export async function inspectZipStructure(file, assetType) {
  const names = await listZipEntryNames(file)
  const checklist = buildChecklistItems(assetType)
  const items = checklist.map((item) => {
    const patternsToMatch = item.kind === 'anyOf' ? item.patterns || [item.pattern] : null
    const ok = patternsToMatch
      ? patternsToMatch.some((pattern) => matchPattern(names, pattern))
      : item.kind === 'includes' && item.pattern === '*'
        ? names.length > 0
        : matchPattern(names, item.pattern)
    return { label: item.label, ok }
  })
  const missing = items.filter((item) => !item.ok).map((item) => item.label)
  const resolved = resolveZipPackageSpec(assetType)
  // 贴纸不看后缀。解压后认文件头：四种图才计数，静动混在一起就拒绝。读不出内容不能算通过。
  if (resolved.minFiles != null && resolved.maxFiles != null) {
    const classified = await classifyStickerZip(file)
    if (classified.error) {
      missing.push(classified.error)
    }
    classified.bad.forEach((message) => missing.push(message))
    const imageCount = classified.images.length
    if (imageCount < resolved.minFiles || imageCount > resolved.maxFiles) {
      const range = resolved.minFiles === resolved.maxFiles
        ? `${resolved.minFiles} 张`
        : `${resolved.minFiles}～${resolved.maxFiles} 张`
      missing.push(`stickers 图需要 ${range}，当前 ${imageCount} 张`)
    }
    const hasAnimated = classified.images.some((image) => image.animated)
    const hasStill = classified.images.some((image) => !image.animated)
    if (hasAnimated && hasStill) {
      missing.push('一个 stickers 包里不能同时有静态图和动态图')
    }
  }
  return {
    ok: missing.length === 0,
    items,
    missing,
    entryCount: names.length,
  }
}

export function formatFileSize(bytes) {
  const n = Number(bytes) || 0
  if (n < 1024) {
    return `${n} B`
  }
  if (n < 1024 * 1024) {
    return `${(n / 1024).toFixed(1)} KB`
  }
  return `${(n / (1024 * 1024)).toFixed(1)} MB`
}
